import { useEffect, useState, useCallback } from 'react';
import { Flame, Timer, X, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DURATION_PRESETS = [
    { label: '24h', hours: 24 },
    { label: '3 days', hours: 72 },
    { label: '7 days', hours: 168 },
    { label: '14 days', hours: 336 },
    { label: '30 days', hours: 720 },
];

const fmtRemaining = (ms) => {
    if (ms <= 0) return '0s';
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
};

export const ReferralBoostPanel = ({ baseReferrer = 10, baseReferee = 0 }) => {
    const [status, setStatus] = useState(null);
    const [referrerBonus, setReferrerBonus] = useState(baseReferrer * 2);
    const [refereeBonus, setRefereeBonus] = useState(baseReferee * 2);
    const [hours, setHours] = useState(168);
    const [loading, setLoading] = useState(false);
    const [now, setNow] = useState(Date.now());

    const fetchStatus = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/referral/boost`, { withCredentials: true });
            setStatus(data);
        } catch (e) {
            console.error('Failed to load boost status', e);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    // Live countdown — ticks every second while a boost is active
    useEffect(() => {
        if (!status?.boost_active) return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [status?.boost_active]);

    // Auto-refresh once the countdown hits zero so the panel flips back to "idle"
    useEffect(() => {
        if (!status?.boost_active || !status?.boost_expires_at) return;
        const expiresMs = new Date(status.boost_expires_at).getTime();
        if (expiresMs - now <= 0) fetchStatus();
    }, [now, status, fetchStatus]);

    const startBoost = async () => {
        if (referrerBonus < 0 || refereeBonus < 0) {
            toast.error('Bonus amounts cannot be negative');
            return;
        }
        if (hours <= 0 || hours > 720) {
            toast.error('Duration must be between 1 hour and 30 days');
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/referral/boost`,
                { referrer_bonus: referrerBonus, referee_bonus: refereeBonus, hours },
                { withCredentials: true }
            );
            setStatus(data);
            toast.success(`Boost is live for ${hours}h — referrer $${referrerBonus} / friend $${refereeBonus}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to start boost');
        } finally {
            setLoading(false);
        }
    };

    const cancelBoost = async () => {
        if (!window.confirm('End the bonus boost now? New signups will revert to the baseline payouts.')) return;
        setLoading(true);
        try {
            const { data } = await axios.delete(`${API}/admin/referral/boost`, { withCredentials: true });
            setStatus(data);
            toast.success('Boost ended');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to cancel boost');
        } finally {
            setLoading(false);
        }
    };

    const expiresMs = status?.boost_expires_at ? new Date(status.boost_expires_at).getTime() : 0;
    const remaining = expiresMs - now;
    const active = status?.boost_active && remaining > 0;

    return (
        <div className="card-dark p-6" data-testid="referral-boost-panel">
            <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                <Flame className="w-5 h-5 text-[#FF6B00]" />
                Bonus Boost
                {active && (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FF6B00]/20 text-[#FF6B00] text-xs font-bold uppercase tracking-wider">
                        Live
                    </span>
                )}
            </h3>
            <p className="text-xs text-[#A0AAB2] mb-4">
                Temporarily increase referral payouts to ignite viral signups. The boost auto-expires —
                no manual cleanup required.
            </p>

            {active ? (
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-[#0a0a0a] border border-[#FF6B00]/30 rounded-lg p-3 text-center">
                            <p className="text-[10px] uppercase text-[#A0AAB2] tracking-wider">Referrer earns</p>
                            <p className="text-2xl font-mono font-bold text-[#FF6B00] mt-1" data-testid="boost-live-referrer">
                                ${status.referrer_bonus}
                            </p>
                            <p className="text-[10px] text-[#A0AAB2] line-through">${status.base_referrer_bonus}</p>
                        </div>
                        <div className="bg-[#0a0a0a] border border-[#39FF14]/30 rounded-lg p-3 text-center">
                            <p className="text-[10px] uppercase text-[#A0AAB2] tracking-wider">Friend gets</p>
                            <p className="text-2xl font-mono font-bold text-[#39FF14] mt-1" data-testid="boost-live-referee">
                                ${status.referee_bonus}
                            </p>
                            <p className="text-[10px] text-[#A0AAB2] line-through">${status.base_referee_bonus}</p>
                        </div>
                        <div className="bg-[#0a0a0a] border border-[#FFD700]/30 rounded-lg p-3 text-center">
                            <p className="text-[10px] uppercase text-[#A0AAB2] tracking-wider flex items-center justify-center gap-1">
                                <Timer className="w-3 h-3" /> Time left
                            </p>
                            <p className="text-xl font-mono font-bold text-[#FFD700] mt-1 tabular-nums" data-testid="boost-countdown">
                                {fmtRemaining(remaining)}
                            </p>
                            <p className="text-[10px] text-[#A0AAB2] truncate">
                                ends {new Date(status.boost_expires_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} CT
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={cancelBoost}
                        disabled={loading}
                        variant="outline"
                        className="w-full bg-transparent border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                        data-testid="boost-cancel-btn"
                    >
                        <X className="w-4 h-4 mr-2" />
                        End Boost Now
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-white text-sm">Boosted Referrer Bonus</Label>
                            <p className="text-xs text-[#A0AAB2] mb-1">Base: ${status?.base_referrer_bonus ?? baseReferrer}</p>
                            <Input
                                type="number"
                                step="1"
                                min="0"
                                value={referrerBonus}
                                onChange={(e) => setReferrerBonus(parseFloat(e.target.value) || 0)}
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="boost-referrer-input"
                            />
                        </div>
                        <div>
                            <Label className="text-white text-sm">Boosted Friend Bonus</Label>
                            <p className="text-xs text-[#A0AAB2] mb-1">Base: ${status?.base_referee_bonus ?? baseReferee}</p>
                            <Input
                                type="number"
                                step="1"
                                min="0"
                                value={refereeBonus}
                                onChange={(e) => setRefereeBonus(parseFloat(e.target.value) || 0)}
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="boost-referee-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-white text-sm">Duration</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {DURATION_PRESETS.map((p) => (
                                <button
                                    key={p.hours}
                                    type="button"
                                    onClick={() => setHours(p.hours)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                                        hours === p.hours
                                            ? 'bg-[#FF6B00] text-white'
                                            : 'bg-white/5 text-[#A0AAB2] hover:bg-white/10 hover:text-white'
                                    }`}
                                    data-testid={`boost-duration-${p.hours}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                            <Input
                                type="number"
                                step="1"
                                min="1"
                                max="720"
                                value={hours}
                                onChange={(e) => setHours(parseInt(e.target.value, 10) || 0)}
                                className="w-24 h-8 bg-[#0a0a0a] border-white/10 text-white text-xs"
                                aria-label="Custom hours"
                                data-testid="boost-hours-input"
                            />
                            <span className="text-xs text-[#A0AAB2] self-center">hours</span>
                        </div>
                    </div>
                    <Button
                        onClick={startBoost}
                        disabled={loading}
                        className="w-full bg-[#FF6B00] hover:bg-[#FF6B00]/80 text-white font-bold"
                        data-testid="boost-start-btn"
                    >
                        <Zap className="w-4 h-4 mr-2" />
                        {loading ? 'Starting…' : `Start Boost — $${referrerBonus} / $${refereeBonus} for ${hours}h`}
                    </Button>
                </div>
            )}
        </div>
    );
};
